import { AVILABLE_PAYMENT_METHOD_COUNT, AVILAIBLE_PAYMENT_METHODS_WITHOUT_OTHER } from '@/constants/payment';
import { PaymentMethodEnum } from '@/types/common';
import type { AvailablePaymentMethod, CheckoutConfigurations, PaymentMethod } from '@/types/checkout';

export const getTopPaymentMethods = (paymentMethods: PaymentMethod[]) => {
  return [...paymentMethods.slice(0, AVILABLE_PAYMENT_METHOD_COUNT)];
};

/**
 * The methods rendered as selectable boxes on the main screen's picker row
 * (DEFAULT template): non-express methods, capped at
 * `AVILABLE_PAYMENT_METHOD_COUNT`. The synthetic "Other" tile may be included
 * here when non-express methods overflow. Callers that only want the "real"
 * top methods (i.e. before the "Other" tile) should use
 * {@link isTopBoxPaymentMethod}, which excludes it.
 */
export const getVisibleBoxPaymentMethods = (
  paymentMethods: AvailablePaymentMethod[]
): AvailablePaymentMethod[] => paymentMethods.filter((pm) => !pm.isExpress).slice(0, AVILABLE_PAYMENT_METHOD_COUNT);

/**
 * True when `paymentMethodName` is one of the top payment-method boxes shown on
 * the main screen (before the synthetic "Other" tile). Drives the "Pay with X"
 * header + security disclaimer surfaced for these primary methods.
 */
export const isTopBoxPaymentMethod = (
  paymentMethodName: PaymentMethodEnum,
  paymentMethods: AvailablePaymentMethod[]
): boolean =>
  paymentMethodName !== PaymentMethodEnum.OTHER &&
  getVisibleBoxPaymentMethods(paymentMethods).some((pm) => pm.paymentMethodName === paymentMethodName);

export const getOtherPaymentMethods = (paymentMethods: AvailablePaymentMethod[]): AvailablePaymentMethod[] => {
  return paymentMethods
    .slice(AVILABLE_PAYMENT_METHOD_COUNT, paymentMethods.length)
    .filter((paymentMethod) => paymentMethod.paymentMethodName !== PaymentMethodEnum.OTHER && !paymentMethod.isExpress);
};

// Synthetic "Other" payment method for UI grouping
const OTHER_PAYMENT_METHOD: AvailablePaymentMethod = {
  id: PaymentMethodEnum.OTHER,
  paymentMethodName: PaymentMethodEnum.OTHER,
  paymentMethodDisplayName: PaymentMethodEnum.OTHER,
  pspPaymentMethodName: null,
  provider: '',
  submidName: '',
  isExpress: false,
};

// Only the native device wallets are express-by-default. Google Pay / Link can
// be downgraded to a normal APM box via config. Everything else — including
// PayPal — is a normal APM box (falls through to `default`). Do NOT special-case
// PayPal here: it is placed by its position in the BE list, exactly like Klarna,
// iDEAL, etc.
const isExpressPaymentMethod = (paymentMethodName: string, checkoutConfig?: CheckoutConfigurations): boolean => {
  switch (paymentMethodName) {
    case PaymentMethodEnum.APPLE_PAY:
      return true;
    case PaymentMethodEnum.GOOGLE_PAY:
      return !checkoutConfig?.shouldShowGooglePayAsApm;
    case PaymentMethodEnum.LINK:
      return !checkoutConfig?.featureFlags?.checkout_display_link_express_as_apm_box;
    default:
      return false;
  }
};

export const organizePaymentMethods = (
  paymentMethods: PaymentMethod[],
  checkoutConfig?: CheckoutConfigurations
): AvailablePaymentMethod[] => {
  const extendedPaymentMethods = paymentMethods
    .filter((pm) => pm.paymentMethodName !== PaymentMethodEnum.OTHER)
    .map((pm) => ({
      ...pm,
      isExpress: isExpressPaymentMethod(pm.paymentMethodName, checkoutConfig),
    }));

  const shouldAddOtherMethod =
    extendedPaymentMethods.filter((pm) => !pm.isExpress).length > AVILABLE_PAYMENT_METHOD_COUNT;
  if (!shouldAddOtherMethod) {
    return extendedPaymentMethods;
  }
  const extendedPaymentMethodsWithoutExpress = extendedPaymentMethods.filter((pm) => !pm.isExpress);
  const extendedPaymentMethodsWithExpress = extendedPaymentMethods.filter((pm) => pm.isExpress);

  const { boxes, dropdown } = splitIntoBoxesAndDropdown(extendedPaymentMethodsWithoutExpress);

  return [...boxes, OTHER_PAYMENT_METHOD, ...dropdown, ...extendedPaymentMethodsWithExpress];
};

/**
 * `organizePaymentMethods` for a flow where EVERY method is a selectable box
 * and nothing goes to the express row — the subscription route, whose
 * Chargebee wallets are drop-in panels a bare-button row can't hold. Shares the
 * overflow → "Other" split (and the card-always-a-box invariant) so
 * `getVisibleBoxPaymentMethods` / `getOtherPaymentMethods` read the same
 * positions they do for `/checkout/v3`.
 */
export const organizeBoxPaymentMethods = (paymentMethods: PaymentMethod[]): AvailablePaymentMethod[] => {
  const boxes = paymentMethods
    .filter((pm) => pm.paymentMethodName !== PaymentMethodEnum.OTHER)
    .map((pm) => ({ ...pm, isExpress: false }));
  if (boxes.length <= AVILABLE_PAYMENT_METHOD_COUNT) {
    return boxes;
  }
  const { boxes: visible, dropdown } = splitIntoBoxesAndDropdown(boxes);
  return [...visible, OTHER_PAYMENT_METHOD, ...dropdown];
};

/**
 * Splits the non-express methods into the visible box row and the "Other"
 * dropdown when they overflow `AVILAIBLE_PAYMENT_METHODS_WITHOUT_OTHER`.
 *
 * Invariant: credit card is ALWAYS a box and never a dropdown item. If it
 * overflowed into the dropdown it's promoted to the last box slot (rendered
 * right before the synthetic "Other" box) and the previously-last box is
 * demoted to the front of the dropdown. Native order is otherwise preserved.
 *
 * Pure: inputs are not mutated.
 */
const splitIntoBoxesAndDropdown = (
  nonExpressMethods: AvailablePaymentMethod[]
): { boxes: AvailablePaymentMethod[]; dropdown: AvailablePaymentMethod[] } => {
  const boxes = nonExpressMethods.slice(0, AVILAIBLE_PAYMENT_METHODS_WITHOUT_OTHER);
  const dropdown = nonExpressMethods.slice(AVILAIBLE_PAYMENT_METHODS_WITHOUT_OTHER);

  const cardIndexInDropdown = dropdown.findIndex((pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD);
  if (cardIndexInDropdown === -1) {
    return { boxes, dropdown };
  }

  const promotedBoxes = [...boxes];
  const nextDropdown = [...dropdown];
  const [cardMethod] = nextDropdown.splice(cardIndexInDropdown, 1);
  const demotedMethod = promotedBoxes.pop();
  if (demotedMethod) {
    nextDropdown.unshift(demotedMethod);
  }
  promotedBoxes.push(cardMethod);

  return { boxes: promotedBoxes, dropdown: nextDropdown };
};

export const getPaymentMethodLogoUrl = (paymentMethodName: string): string => {
  return `https://media.appcharge.com/defaults/apm_logos/v3/logo/${paymentMethodName}.svg`;
};

export const getPaymentMethodBoxUrl = (paymentMethodName: string): string => {
  return `https://media.appcharge.com/defaults/apm_logos/v3/box/${paymentMethodName}.svg`;
};

// Canonical names use snake_case (e.g. `amazon_pay`) and Nuvei's `apmgw_*`
// prefix; testids are kebab-cased and lowercased so selectors stay readable.
export const getPaymentMethodTestId = (paymentMethodName: string): string =>
  `payment-method-${paymentMethodName.toLowerCase().replace(/_/g, '-')}`;

// Card-style methods render a text label next to the icon (most APMs are
// icon-only). They share the same i18n `creditCard` label unless
// `shouldUseBeDisplayName` opts them out.
const CARD_PAYMENT_METHODS: ReadonlySet<string> = new Set<string>([
  PaymentMethodEnum.CREDIT_CARD,
  PaymentMethodEnum.KR_CARD,
]);

export const isCardPaymentMethod = (paymentMethodName: string): boolean => CARD_PAYMENT_METHODS.has(paymentMethodName);

// Methods whose box renders the BE-supplied `paymentMethodDisplayName` verbatim,
// because the wire already says it better than we can: KR Card's payload is
// locale-correct and would otherwise collide with the generic "Card" row
// (ACDEV-25680). It keeps its icon — whether the name sits beside a logo or
// replaces it follows from `isCardPaymentMethod`.
const BE_DISPLAY_NAME_PAYMENT_METHODS: ReadonlySet<string> = new Set<string>([PaymentMethodEnum.KR_CARD]);

export const shouldUseBeDisplayName = (paymentMethodName: string): boolean =>
  BE_DISPLAY_NAME_PAYMENT_METHODS.has(paymentMethodName);
